import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Input } from '../components/ui/input';
import {
  Music, MapPin, Search, Users, Disc3, Flame, Clock,
  ChevronRight, Loader2, Headphones, SlidersHorizontal
} from 'lucide-react';
import { API } from '../config/api';

const GENRES = [
  'Hip-Hop', 'R&B', 'Trap', 'Pop', 'Afrobeats', 'Go-Go',
  'Drill', 'Lo-Fi', 'Soul', 'Dance', 'Electronic', 'Rock'
];
const SORT_OPTIONS = [
  { value: 'newest', label: 'Newest', icon: Clock },
  { value: 'trending', label: 'Trending', icon: Flame },
  { value: 'popular', label: 'Most Followed', icon: Users },
];

function ProducerCard({ producer }) {
  return (
    <Link to={`/producers/${producer.handle}`} data-testid={`producer-card-${producer.user_id}`}>
      <Card className="group bg-muted/20 border-border hover:border-orange-500/30 transition-all duration-300 overflow-hidden h-full">
        {/* Avatar section */}
        <div className="p-5 flex flex-col items-center text-center">
          {producer.avatar_url ? (
            <img src={producer.avatar_url} alt={producer.name} className="w-20 h-20 rounded-2xl object-cover mb-3 group-hover:scale-105 transition-transform border-2 border-border" />
          ) : (
            <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-orange-600/30 to-amber-700/20 flex items-center justify-center mb-3 border-2 border-border">
              <Music className="w-8 h-8 text-orange-400/60" />
            </div>
          )}
          <h3 className="font-semibold text-sm text-foreground truncate w-full">{producer.name}</h3>
          <p className="text-xs text-muted-foreground truncate w-full">{producer.handle ? `@${producer.handle}` : ""}</p>

          {producer.location && (
            <p className="text-xs text-muted-foreground flex items-center gap-1 mt-1">
              <MapPin className="w-3 h-3" />{producer.location}
            </p>
          )}

          {producer.genres?.length > 0 && (
            <div className="flex flex-wrap gap-1 justify-center mt-2">
              {producer.genres.slice(0, 2).map((g, i) => (
                <Badge key={i} variant="outline" className="text-[10px] border-border text-muted-foreground py-0">{g}</Badge>
              ))}
            </div>
          )}

          <div className="flex items-center gap-3 text-xs text-muted-foreground mt-3">
            <span className="flex items-center gap-1"><Users className="w-3 h-3" />{producer.followers_count || 0}</span>
            <span className="flex items-center gap-1"><Disc3 className="w-3 h-3" />{producer.beat_count || 0}</span>
          </div>

          {producer.work_with_me && (
            <Badge className="bg-green-500/10 text-green-400 border-green-500/20 text-[10px] mt-2">Open to Work</Badge>
          )}
        </div>
      </Card>
    </Link>
  );
}

function TrendingBeatCard({ beat }) {
  return (
    <Link to={`/producers/${beat.user_id}`} className="flex-shrink-0 w-40" data-testid={`trending-beat-${beat.beat_id}`}>
      <div className="rounded-xl overflow-hidden bg-muted/20 border border-border hover:border-orange-500/20 transition-all">
        <div className="aspect-square relative">
          {beat.cover_art_url ? (
            <img src={beat.cover_art_url} alt={beat.title} className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-orange-900/30 to-amber-900/10 flex items-center justify-center">
              <Disc3 className="w-8 h-8 text-orange-400/40" />
            </div>
          )}
          {beat.price && !beat.is_free && (
            <div className="absolute top-1.5 right-1.5 bg-background/70 text-[10px] px-1.5 py-0.5 rounded-full text-orange-300">${beat.price}</div>
          )}
        </div>
        <div className="p-2">
          <p className="text-xs font-medium truncate">{beat.title}</p>
          <p className="text-[10px] text-muted-foreground truncate">{beat.producer_name}</p>
          <div className="flex items-center gap-2 text-[10px] text-muted-foreground/80 mt-1">
            <span className="flex items-center gap-0.5"><Headphones className="w-2.5 h-2.5" />{beat.plays_count || 0}</span>
            {beat.bpm && <span>{beat.bpm}bpm</span>}
          </div>
        </div>
      </div>
    </Link>
  );
}

export default function FindProducersPage() {
  const [producers, setProducers] = useState([]);
  const [trendingBeats, setTrendingBeats] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [genre, setGenre] = useState('');
  const [sort, setSort] = useState('newest');
  const [showFilters, setShowFilters] = useState(false);

  useEffect(() => { fetchProducers(); fetchTrending(); }, [genre, sort]);

  const fetchProducers = async (q) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: '40', sort });
      if (genre) params.set('genre', genre);
      if (q || search) params.set('search', q || search);
      const res = await fetch(`${API}/producers?${params}`);
      if (res.ok) {
        const data = await res.json();
        setProducers(data.producers || []);
      }
    } catch {} finally { setLoading(false); }
  };

  const fetchTrending = async () => {
    try {
      const res = await fetch(`${API}/producers/trending/beats?limit=10`);
      if (res.ok) {
        const data = await res.json();
        setTrendingBeats(data.beats || []);
      }
    } catch {}
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchProducers(search);
  };

  return (
    <div className="min-h-screen pb-20" data-testid="find-producers-page">
      {/* Hero */}
      <div className="relative py-12 md:py-16 px-4">
        <div className="absolute inset-0 bg-gradient-to-b from-orange-900/10 to-transparent pointer-events-none" />
        <div className="max-w-5xl mx-auto relative">
          <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-2" data-testid="page-title">
            Find Producers
          </h1>
          <p className="text-muted-foreground text-sm md:text-base mb-6 max-w-lg">
            Discover talented beatmakers in the DMV area. Browse catalogs, preview beats, and connect directly.
          </p>

          {/* Search */}
          <form onSubmit={handleSearch} className="flex gap-2 max-w-lg">
            <div className="flex-1 relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                data-testid="producer-search-input"
                placeholder="Search producers, genres..."
                value={search}
                onChange={e => setSearch(e.target.value)}
                className="pl-9 bg-card/80 border-border h-10"
              />
            </div>
            <Button type="submit" size="sm" className="bg-orange-600 hover:bg-orange-700 h-10 px-4">Search</Button>
            <Button type="button" variant="outline" size="icon" className="border-border h-10 w-10" onClick={() => setShowFilters(!showFilters)}>
              <SlidersHorizontal className="w-4 h-4" />
            </Button>
          </form>

          {/* Filters */}
          {showFilters && (
            <div className="mt-4 p-4 rounded-xl bg-card/60 border border-border max-w-lg" data-testid="filters-panel">
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Genre</p>
              <div className="flex flex-wrap gap-1.5 mb-4">
                <Badge
                  className={`cursor-pointer text-xs ${!genre ? 'bg-orange-600 text-white' : 'bg-muted text-muted-foreground hover:bg-muted'}`}
                  onClick={() => setGenre('')}
                >All</Badge>
                {GENRES.map(g => (
                  <Badge
                    key={g}
                    className={`cursor-pointer text-xs ${genre === g ? 'bg-orange-600 text-white' : 'bg-muted text-muted-foreground hover:bg-muted'}`}
                    onClick={() => setGenre(genre === g ? '' : g)}
                  >{g}</Badge>
                ))}
              </div>
              <p className="text-xs text-muted-foreground uppercase tracking-wider mb-2">Sort By</p>
              <div className="flex gap-2">
                {SORT_OPTIONS.map(s => (
                  <Button
                    key={s.value}
                    variant={sort === s.value ? 'default' : 'outline'}
                    size="sm"
                    className={sort === s.value ? 'bg-orange-600 hover:bg-orange-700' : 'border-border text-muted-foreground'}
                    onClick={() => setSort(s.value)}
                  >
                    <s.icon className="w-3.5 h-3.5 mr-1.5" />{s.label}
                  </Button>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="max-w-5xl mx-auto px-4">
        {/* Trending Beats Horizontal Scroll */}
        {trendingBeats.length > 0 && (
          <div className="mb-10">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-bold text-white flex items-center gap-2"><Flame className="w-4 h-4 text-orange-400" />Trending Beats</h2>
            </div>
            <div className="flex gap-3 overflow-x-auto pb-2 scrollbar-hide" data-testid="trending-beats-row">
              {trendingBeats.map(b => <TrendingBeatCard key={b.beat_id} beat={b} />)}
            </div>
          </div>
        )}

        {/* Producer Grid */}
        {loading ? (
          <div className="flex justify-center py-16"><Loader2 className="w-8 h-8 animate-spin text-orange-400" /></div>
        ) : producers.length === 0 ? (
          <div className="text-center py-16" data-testid="no-producers-message">
            <Music className="w-12 h-12 mx-auto mb-3 text-muted-foreground/80" />
            <p className="text-muted-foreground mb-1">No producers found</p>
            <p className="text-muted-foreground/80 text-sm">Try adjusting your search or filters</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-3" data-testid="producers-grid">
            {producers.map(p => <ProducerCard key={p.user_id} producer={p} />)}
          </div>
        )}
      </div>
    </div>
  );
}
